import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { 
  ArrowRight, CheckCircle2, FileText, 
  AlertTriangle, Zap, Clock, DollarSign,
  ChevronRight, Truck, Receipt, CreditCard, 
  Calculator, Share2, Eye, Send,
  Camera, Mic, Hash, MessageSquare, ClipboardCheck,
  Users, Image, CalendarClock, FileCheck, Shield,
  PenLine, Building2, AlertCircle, Trophy
} from 'lucide-react';
import RequestSetupModal from '../components/ui/request-setup-modal';
import ShareModal from '../components/ui/share-modal';

// Before/After Mockup Component for Variations
const BeforeAfterMockup = () => {
  return (
    <div className="w-full max-w-5xl mx-auto">
      {/* Desktop Layout */}
      <div className="hidden md:flex items-center justify-between gap-4">
        {/* Before - Unrecorded */}
        <motion.div 
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="flex-1 bg-slate-100 rounded-2xl p-6 border-2 border-slate-200"
        >
          <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4">Before 2Tap...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <MessageSquare className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Verbal Agreements</p>
                <p className="text-xs text-slate-500">"Builder said it's fine..."</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <Clock className="w-8 h-8 text-amber-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Remembered Later</p>
                <p className="text-xs text-slate-500">Details get fuzzy over time</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <DollarSign className="w-8 h-8 text-red-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Disputed Costs</p>
                <p className="text-xs text-slate-500">"That wasn't in the quote"</p>
              </div>
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm">
              <AlertTriangle className="w-8 h-8 text-slate-400" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">No Paper Trail</p>
                <p className="text-xs text-slate-500">Your word against theirs</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Arrow */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.5 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
          className="flex flex-col items-center gap-2"
        >
          <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center shadow-lg">
            <Zap className="w-8 h-8 text-white" />
          </div>
          <p className="text-xs font-bold text-blue-500 text-center">2TAP<br/>CAPTURES</p>
        </motion.div>

        {/* After - 2Tap Output */}
        <motion.div 
          initial={{ opacity: 0, x: 20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="flex-1 bg-gradient-to-br from-blue-50 to-white rounded-2xl p-6 border-2 border-blue-200"
        >
          <div className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-4">2Tap creates...</div>
          <div className="space-y-3">
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <FileCheck className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Documented Record</p>
                <p className="text-xs text-blue-600">Timestamped & saved</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Image className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Photo Evidence</p>
                <p className="text-xs text-blue-600">Before & after captured</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <DollarSign className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Cost Impact Noted</p>
                <p className="text-xs text-blue-600">Clear pricing attached</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
            <div className="flex items-center gap-3 bg-white rounded-lg p-3 shadow-sm border border-blue-100">
              <Send className="w-8 h-8 text-blue-500" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-slate-800">Ready to Share</p>
                <p className="text-xs text-blue-600">Send for approval</p>
              </div>
              <CheckCircle2 className="w-5 h-5 text-blue-500" />
            </div>
          </div>
        </motion.div>
      </div>

      {/* Mobile Layout */}
      <div className="md:hidden space-y-4">
        <div className="bg-slate-100 rounded-xl p-4 border border-slate-200">
          <p className="text-xs font-bold text-slate-500 mb-3">BEFORE</p>
          <div className="flex items-center gap-3">
            <MessageSquare className="w-10 h-10 text-red-500" />
            <div>
              <p className="text-sm font-semibold">Verbal agreements</p>
              <p className="text-xs text-slate-500">No proof, no payment</p>
            </div>
          </div>
        </div>
        
        <div className="flex justify-center">
          <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
        </div>
        
        <div className="bg-gradient-to-br from-blue-50 to-white rounded-xl p-4 border border-blue-200">
          <p className="text-xs font-bold text-blue-600 mb-3">AFTER</p>
          <div className="flex items-center gap-3">
            <FileCheck className="w-10 h-10 text-blue-500" />
            <div>
              <p className="text-sm font-semibold">Documented variation</p>
              <p className="text-xs text-blue-600">Photos, notes & cost</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Example Variation Output Component
const ExampleVariationOutput = () => {
  const [activeTab, setActiveTab] = useState('details');

  return (
    <div className="grid md:grid-cols-2 gap-8 items-start">
      {/* Left - Input */}
      <motion.div
        initial={{ opacity: 0, x: -20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-slate-100 rounded-2xl p-6"
      >
        <div className="text-xs font-bold text-slate-500 uppercase tracking-wider mb-4 flex items-center gap-2">
          <Mic className="w-4 h-4" />
          YOU TELL 2TAP
        </div>
        
        <div className="bg-white rounded-xl p-4 shadow-sm mb-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center flex-shrink-0">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs text-slate-500 mb-1">Voice note transcription:</p>
              <p className="text-sm text-slate-700 italic">
                "Hey, got a variation on the Smith job. Builder wants us to add an extra 
                downlight in the kitchen - wasn't on the original plans. It's gonna be 
                about 2 hours extra labour plus the fitting. Maybe $350 all up. 
                Spoke to Dave on site, he said go ahead but I want it recorded."
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <Camera className="w-4 h-4 text-slate-400" />
            <span>+ Photo of location attached</span>
          </div>
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <FileText className="w-4 h-4 text-slate-400" />
            <span>+ Original plan markup</span>
          </div>
        </div>
      </motion.div>

      {/* Right - Output with Tabs */}
      <motion.div
        initial={{ opacity: 0, x: 20 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        className="bg-white rounded-2xl shadow-lg border border-blue-100 overflow-hidden"
      >
        {/* Tab Header */}
        <div className="flex border-b border-slate-200 bg-slate-50">
          {[
            { id: 'details', label: 'Variation Details', icon: FileCheck },
            { id: 'cost', label: 'Cost & Time', icon: DollarSign },
            { id: 'approval', label: 'Approval', icon: PenLine }
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-semibold transition-colors ${
                activeTab === tab.id 
                  ? 'text-blue-600 bg-white border-b-2 border-blue-500' 
                  : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="p-4">
          {activeTab === 'details' && (
            <div className="space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 border border-blue-100">
                <p className="text-xs font-bold text-blue-700 mb-2 flex items-center gap-1.5">
                  <Hash className="w-3.5 h-3.5" />
                  VARIATION: VAR-0023
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Job:</span> 
                    <span className="font-medium">Smith Residence - Electrical</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Category:</span> 
                    <span className="font-medium bg-amber-100 text-amber-700 px-2 py-0.5 rounded">Variation</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Requested by:</span> 
                    <span className="font-medium">Dave (Site Super)</span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Date:</span> 
                    <span className="font-medium">8 Jul 2026, 2:34 PM</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Description</p>
                <p className="text-xs text-slate-600 leading-relaxed">
                  Add additional downlight in kitchen area - not included in original electrical plans. 
                  Location: above island bench. Includes supply and install of LED downlight fitting.
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Photos</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                    <Camera className="w-6 h-6 text-slate-400" />
                  </div>
                  <div className="aspect-video bg-slate-100 rounded-lg flex items-center justify-center">
                    <Image className="w-6 h-6 text-slate-400" />
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'cost' && (
            <div className="space-y-3">
              <div className="bg-green-50 rounded-lg p-3 border border-green-100">
                <p className="text-xs font-bold text-green-700 mb-2 flex items-center gap-1.5">
                  <DollarSign className="w-3.5 h-3.5" />
                  COST BREAKDOWN
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="space-y-2">
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-xs text-slate-600">Labour (2 hrs @ $85/hr)</span>
                    <span className="text-xs font-semibold text-slate-800">$170.00</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-xs text-slate-600">LED Downlight Fitting</span>
                    <span className="text-xs font-semibold text-slate-800">$120.00</span>
                  </div>
                  <div className="flex items-center justify-between py-1.5 border-b border-slate-100">
                    <span className="text-xs text-slate-600">Cabling & Consumables</span>
                    <span className="text-xs font-semibold text-slate-800">$60.00</span>
                  </div>
                  <div className="flex items-center justify-between py-2 bg-green-50 rounded px-2 mt-2">
                    <span className="text-sm font-bold text-green-700">Total</span>
                    <span className="text-sm font-bold text-green-700">$350.00</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Time Impact</p>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5 text-blue-500" />
                  <div>
                    <p className="text-sm font-semibold text-slate-800">+2 hours</p>
                    <p className="text-[10px] text-slate-500">Added to project timeline</p>
                  </div>
                </div>
              </div>

              <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />
                  PAYMENT TERMS
                </p>
                <p className="text-xs text-amber-800">To be invoiced with final claim</p>
              </div>
            </div>
          )}

          {activeTab === 'approval' && (
            <div className="space-y-3">
              <div className="bg-purple-50 rounded-lg p-3 border border-purple-100">
                <p className="text-xs font-bold text-purple-700 mb-2 flex items-center gap-1.5">
                  <PenLine className="w-3.5 h-3.5" />
                  APPROVAL STATUS
                </p>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <div className="flex items-center justify-between mb-3">
                  <span className="text-xs font-medium text-slate-600">Current Status</span>
                  <span className="bg-amber-100 text-amber-700 text-[10px] font-semibold px-2 py-1 rounded">Pending Approval</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Variation documented</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Photos attached</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-green-500" />
                    <span>Cost breakdown added</span>
                  </div>
                  <div className="flex items-center gap-2 text-xs text-slate-400">
                    <Clock className="w-4 h-4" />
                    <span>Awaiting builder sign-off</span>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Send For Approval</p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="bg-blue-100 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-200 transition-colors">
                    <MessageSquare className="w-5 h-5 text-blue-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-blue-700">SMS</p>
                  </div>
                  <div className="bg-green-100 rounded-lg p-3 text-center cursor-pointer hover:bg-green-200 transition-colors">
                    <Send className="w-5 h-5 text-green-600 mx-auto mb-1" />
                    <p className="text-xs font-semibold text-green-700">Email</p>
                  </div>
                </div>
              </div>

              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-[10px] font-bold text-slate-500 uppercase tracking-wider mb-2">Approval History</p>
                <div className="text-xs text-slate-500 italic">
                  No approvals yet - send to builder for sign-off
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Next Action */}
        <div className="p-4 pt-0">
          <div className="bg-blue-500 rounded-lg p-3 text-center cursor-pointer hover:bg-blue-600 transition-colors">
            <p className="text-xs font-bold text-white flex items-center justify-center gap-2">
              Save & Send for Approval
              <Send className="w-4 h-4" />
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

// Workflow Step Component
const WorkflowStep = ({ icon: Icon, label, isActive, isCompleted }) => (
  <div className={`flex flex-col items-center gap-2 ${isActive ? 'opacity-100' : 'opacity-60'}`}>
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${
      isActive ? 'bg-blue-500 text-white' : 
      isCompleted ? 'bg-green-500 text-white' :
      'bg-slate-200 text-slate-500'
    }`}>
      <Icon className="w-6 h-6" />
    </div>
    <p className={`text-xs font-medium text-center max-w-[80px] ${isActive ? 'text-blue-600' : 'text-slate-600'}`}>
      {label}
    </p>
  </div>
);

// Main Page Component
const FeatureVariations = () => {
  const navigate = useNavigate();
  const [showSetupModal, setShowSetupModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);

  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Navigation links for related areas
  const relatedAreas = [
    { icon: Calculator, label: 'Scope & Quotes', href: '/features/scope-costing-quote', color: 'emerald' },
    { icon: ClipboardCheck, label: 'SWMS & Safety', href: '/features/swms-safety', color: 'blue' },
    { icon: Truck, label: 'Site Updates', href: '/features/site-updates', color: 'blue' },
    { icon: AlertTriangle, label: 'Variations', href: '/features/variations', color: 'blue', active: true },
    { icon: Receipt, label: 'Invoicing', href: '/features', color: 'orange' },
    { icon: CreditCard, label: 'Payments', href: '/features', color: 'orange' }
  ];

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Manrope, sans-serif' }}>
      {/* Navigation - Consistent with Landing Page */}
      <nav className="fixed top-0 left-0 right-0 bg-white/80 backdrop-blur-xl border-b border-slate-200 z-50">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 flex items-center justify-between">
          <div 
            className="flex items-center gap-2 cursor-pointer" 
            onClick={() => navigate('/')}
          >
            <div className="w-10 h-10 bg-[#1F2A37] rounded-lg flex items-center justify-center">
              <span className="text-orange-500 font-bold text-xl" style={{ fontFamily: 'Chivo, sans-serif' }}>2</span>
            </div>
            <span className="font-bold text-xl text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>2TAP</span>
          </div>
          
          <div className="hidden md:flex items-center gap-8">
            <button 
              onClick={() => navigate('/')}
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide"
            >
              HOME
            </button>
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-sm font-medium tracking-wide"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="text-slate-600 hover:text-slate-900 text-sm font-medium tracking-wide flex items-center gap-1.5"
            >
              <Share2 className="w-4 h-4" />
              SHARE
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm px-6 font-semibold tracking-wide shadow-[3px_3px_0px_rgba(0,0,0,0.2)]"
            >
              LOG IN
            </Button>
          </div>

          <div className="md:hidden flex items-center gap-1">
            <button 
              onClick={() => navigate('/features')}
              className="text-orange-500 text-xs font-medium px-2"
            >
              FEATURES
            </button>
            <button 
              onClick={() => setShowShareModal(true)} 
              className="p-1.5 text-slate-600 hover:text-slate-900"
            >
              <Share2 className="w-5 h-5" />
            </button>
            <Button 
              onClick={() => navigate('/login')} 
              size="sm" 
              className="bg-orange-500 hover:bg-orange-600 text-white rounded-sm text-xs px-3"
            >
              LOG IN
            </Button>
          </div>
        </div>
      </nav>

      {/* 1. Hero Section */}
      <section className="relative pt-24 pb-16 md:pt-32 md:pb-24 px-4 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-64 h-64 bg-blue-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-cyan-500 rounded-full blur-3xl"></div>
        </div>
        
        <div className="relative max-w-6xl mx-auto">
          {/* Feature Label */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 bg-blue-500/20 border border-blue-500/30 text-blue-400 px-3 py-1.5 rounded-full text-xs font-semibold mb-6 tracking-wider"
          >
            <AlertTriangle className="w-4 h-4" />
            VARIATION CAPTURE
          </motion.div>

          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Text */}
            <motion.div 
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1 }}
            >
              {/* Hook */}
              <p className="text-blue-400 text-sm md:text-base font-semibold mb-3">
                Builder wants extra work? Changes on site?
              </p>
              
              {/* Headline */}
              <h1 
                className="text-3xl sm:text-4xl md:text-5xl font-black text-white leading-tight mb-6"
                style={{ fontFamily: 'Chivo, sans-serif' }}
              >
                CAPTURE VARIATIONS<br />
                <span className="text-blue-500">BEFORE THEY'RE FORGOTTEN</span>
              </h1>

              {/* Subheading */}
              <p className="text-slate-300 text-sm md:text-base mb-4 leading-relaxed">
                Document every change request with photos, voice notes, and cost estimates. 
                Get it on record instantly — so you actually get paid for extra work.
              </p>

              {/* Supporting Line */}
              <p className="text-slate-400 text-sm border-l-2 border-blue-500 pl-3 mb-8">
                No more "that wasn't in the quote" disputes. Every variation is timestamped and traceable.
              </p>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-3">
                <Button 
                  onClick={() => setShowSetupModal(true)}
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-6 py-3 font-bold"
                >
                  REGISTER INTEREST
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>

            {/* Right - Visual Mockup */}
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.3 }}
              className="hidden md:block"
            >
              <BeforeAfterMockup />
            </motion.div>
          </div>

          {/* Mobile Mockup */}
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="md:hidden mt-10"
          >
            <BeforeAfterMockup />
          </motion.div>
        </div>
      </section>

      {/* 2. Problem Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-100">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 text-red-500 text-xs font-bold tracking-widest mb-4">
              <AlertCircle className="w-4 h-4" />
              THE PROBLEM
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900 mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              VERBAL VARIATIONS<br />
              <span className="text-red-500">DON'T GET PAID</span>
            </h2>
            <p className="text-slate-600 text-sm md:text-base leading-relaxed max-w-2xl mx-auto">
              Builder asks for extra work. You say yes. End of job comes around, and suddenly 
              "that was included" or "we never agreed to that." Without proof, you're working for free.
            </p>
          </motion.div>

          {/* Pain Points */}
          <div className="grid sm:grid-cols-3 gap-4 mt-10">
            {[
              { icon: MessageSquare, text: 'He said, she said' },
              { icon: DollarSign, text: 'Unpaid extras' },
              { icon: Clock, text: 'Forgotten details' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white rounded-xl p-4 shadow-sm"
              >
                <item.icon className="w-8 h-8 text-red-500 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-700">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 3. What 2Tap Creates Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <CheckCircle2 className="w-4 h-4" />
              WHAT 2TAP CREATES
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              DOCUMENTED VARIATIONS<br />
              <span className="text-blue-500">READY FOR APPROVAL</span>
            </h2>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { 
                icon: FileCheck, 
                title: 'Variation Record', 
                desc: 'Timestamped documentation of what changed and why',
                color: 'blue'
              },
              { 
                icon: Image, 
                title: 'Photo Evidence', 
                desc: 'Before and after photos attached automatically',
                color: 'purple'
              },
              { 
                icon: DollarSign, 
                title: 'Cost Breakdown', 
                desc: 'Labour, materials, and total impact calculated',
                color: 'green'
              },
              { 
                icon: Send, 
                title: 'Approval Workflow', 
                desc: 'Send to builder for sign-off via SMS or email',
                color: 'orange'
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-gradient-to-br from-slate-50 to-white rounded-2xl p-6 border border-slate-200 shadow-sm"
              >
                <div className="w-14 h-14 bg-blue-500 rounded-xl flex items-center justify-center mb-4">
                  <item.icon className="w-7 h-7 text-white" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. How It Works Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-50">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-blue-500 text-xs font-bold tracking-widest mb-4">
              <Zap className="w-4 h-4" />
              HOW IT WORKS
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              CAPTURE IT ON SITE<br />
              <span className="text-blue-500">IN SECONDS</span>
            </h2>
          </motion.div>

          <div className="space-y-6">
            {[
              { 
                step: '1', 
                title: 'Spot the Change', 
                desc: 'Builder asks for something extra, or you find something that wasn\'t in the scope. Open 2Tap.' 
              },
              { 
                step: '2', 
                title: 'Record It', 
                desc: 'Speak what changed, snap a photo, add the cost impact. All captured with a timestamp.' 
              },
              { 
                step: '3', 
                title: 'Send for Approval', 
                desc: 'Fire it off to the builder via SMS or email. Get their sign-off before you do the work.' 
              }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, x: i % 2 === 0 ? -20 : 20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="flex items-start gap-4 bg-white rounded-xl p-5 shadow-sm"
              >
                <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-black text-xl">{item.step}</span>
                </div>
                <div>
                  <h3 className="text-lg font-bold text-slate-900 mb-1">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Example Output Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 text-slate-500 text-xs font-bold tracking-widest mb-4">
              <FileText className="w-4 h-4" />
              EXAMPLE
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              SEE THE<br />
              <span className="text-blue-500">OUTPUT</span>
            </h2>
          </motion.div>

          <ExampleVariationOutput />
        </div>
      </section>

      {/* 6. Why It Matters Section */}
      <section className="py-16 md:py-24 px-4 bg-gradient-to-br from-blue-600 to-blue-700">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-6"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHY IT MATTERS
            </h2>
            <p className="text-blue-100 text-lg md:text-xl mb-10 max-w-2xl mx-auto">
              Every undocumented variation is money you might never see. Protect your work. Get it on record.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Shield, title: 'Protect Your Work', desc: 'Proof that changes were agreed' },
              { icon: DollarSign, title: 'Get Paid', desc: 'No more disputed extras' },
              { icon: Clock, title: 'Save Arguments', desc: 'Clear records end disputes fast' }
            ].map((item, i) => (
              <motion.div 
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-white/10 backdrop-blur-sm rounded-xl p-5"
              >
                <item.icon className="w-10 h-10 text-white mx-auto mb-3" />
                <h3 className="text-lg font-bold text-white mb-1">{item.title}</h3>
                <p className="text-sm text-blue-100">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 7. Where It Fits Section */}
      <section className="py-16 md:py-24 px-4 bg-white">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <div className="inline-flex items-center gap-2 bg-blue-100 text-blue-700 px-3 py-1.5 rounded-full text-xs font-bold mb-4">
              <Truck className="w-4 h-4" />
              PART OF DELIVER THE WORK
            </div>
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-slate-900"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              WHERE IT FITS IN<br />
              <span className="text-blue-500">2TAP</span>
            </h2>
          </motion.div>

          {/* Workflow */}
          <div className="bg-slate-50 rounded-2xl p-6 md:p-8 shadow-sm mb-10">
            <div className="flex flex-wrap justify-center items-center gap-2 md:gap-4">
              <WorkflowStep icon={ClipboardCheck} label="SWMS Signed" isCompleted />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Truck} label="Work in Progress" isCompleted />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={AlertTriangle} label="Variation Captured" isActive />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={CheckCircle2} label="Approved" />
              <ChevronRight className="w-5 h-5 text-slate-300 hidden sm:block" />
              <WorkflowStep icon={Receipt} label="Invoice" />
            </div>
          </div>

          {/* Related Areas */}
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {relatedAreas.map((item, i) => (
              <div 
                key={i} 
                onClick={() => navigate(item.href)}
                className={`flex items-center gap-2 rounded-lg p-3 cursor-pointer transition-all hover:scale-[1.02] ${
                  item.active 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white border border-slate-200 text-slate-700 hover:border-slate-300 hover:shadow-sm'
                }`}
              >
                <item.icon className={`w-5 h-5 ${item.active ? 'text-white' : 'text-blue-500'}`} />
                <span className="text-sm font-medium">{item.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. Final CTA Section */}
      <section className="py-16 md:py-24 px-4 bg-slate-900">
        <div className="max-w-3xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 
              className="text-2xl sm:text-3xl md:text-4xl font-black text-white mb-4"
              style={{ fontFamily: 'Chivo, sans-serif' }}
            >
              READY TO STOP<br />
              <span className="text-blue-500">LOSING MONEY ON EXTRAS?</span>
            </h2>
            <p className="text-slate-400 text-sm md:text-base mb-8 max-w-xl mx-auto">
              Every undocumented variation is cash you'll never see. The smart move? Get set up with 2Tap and protect every dollar.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button 
                onClick={() => setShowSetupModal(true)}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-sm px-8 py-4 font-bold text-lg"
              >
                REGISTER INTEREST
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-6 px-4 bg-slate-950">
        <div className="max-w-5xl mx-auto flex flex-col items-center gap-4">
          <button 
            onClick={() => setShowSetupModal(true)}
            className="text-slate-400 hover:text-orange-400 text-sm transition-colors"
          >
            Want a custom 2Tap for your business or industry? <span className="underline">Let's talk →</span>
          </button>
          <div className="w-full flex flex-col sm:flex-row items-center justify-between gap-4">
            <p className="text-slate-500 text-sm">
              © 2025 2Tap. Powered for Smart Business.
            </p>
            <div className="flex items-center gap-6">
              <button onClick={() => { window.scrollTo(0, 0); navigate('/'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Home</button>
              <button onClick={() => { window.scrollTo(0, 0); navigate('/features'); }} className="text-slate-400 hover:text-white text-sm transition-colors">Features</button>
              <button onClick={() => setShowShareModal(true)} className="text-slate-400 hover:text-white text-sm transition-colors">Share</button>
            </div>
          </div>
        </div>
      </footer>

      {/* Setup Modal */}
      <RequestSetupModal 
        isOpen={showSetupModal} 
        onClose={() => setShowSetupModal(false)} 
      />

      {/* Share Modal */}
      <ShareModal 
        isOpen={showShareModal} 
        onClose={() => setShowShareModal(false)} 
      />
    </div>
  );
};

export default FeatureVariations;
